/* eslint-disable max-lines-per-function */
import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { getMonthNameByDateObject } from '@/utils';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { Calendar } from './Calendar/Calendar';
import { DayProps } from 'react-day-picker';
import { useDialogContext } from './DialogContext';
import { useRouteParams } from './useRouteParams';
import { Day } from './Calendar/Day';
import { useTagebuchEinstellungen } from '@/domain/controlling/hooks/useTagebuchEinstellungen';
import { Header } from './Header';
import { EditTable } from './EditTable';
import { DialogFooterMessage } from '../../components';
import { Button } from '@/components/ui/button';
import { isPlanwertValid } from './helpers';
import { usePlanwerte } from '@/domain/controlling/hooks';
import { useFormSubmit } from './useFormSubmit';
import { useNavigate } from 'react-router-dom';
import { Dispatch, SetStateAction, Suspense, useEffect } from 'react';
import { TagesInformationen } from './TagesInformationen';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { Mindestbelegung } from '@/domain/controlling/pages/Umsatzplanung/Planung/Dialog/Mindestbelegung/Mindestbelegung';
import { WithFeatureFlags } from '@/features/featureFlags/WithFeatureFlags';

export function EditForm({
  setHasChanges,
}: {
  setHasChanges: Dispatch<SetStateAction<boolean>>;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { searchParams, routeParams } = useRouteParams();
  const { datum } = searchParams;
  const { dispatch, planwerteForm } = useDialogContext();
  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { planwerte } = usePlanwerte({
    jahrMonat: {
      jahr: routeParams.jahr,
      monat: routeParams.monat,
    },
    standortId,
  });
  const { tagebuchEinstellungen, umsatzplanungAnzeige } =
    useTagebuchEinstellungen(standortId);

  const daysOfMonth = Array.from(
    { length: dayjs(datum).daysInMonth() },
    (_, i) =>
      dayjs(new Date(routeParams.jahr, routeParams.monat - 1, i + 1)).format(
        'YYYY-MM-DD',
      ),
  );

  const validPlanwerteCount = daysOfMonth.reduce((acc, day) => {
    const currentPlanwert = planwerte.find(
      (planwert) => planwert.datum === day,
    );
    if (
      isPlanwertValid(
        umsatzplanungAnzeige,
        tagebuchEinstellungen.absatzProduktivitaet.absatzVorgabe,
        currentPlanwert,
      )
    ) {
      return acc + 1;
    }
    return acc;
  }, 0);

  function handleClose() {
    dispatch({ type: 'reset' });
    navigate(
      `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}`,
    );
  }

  function onSuccess() {
    dispatch({ type: 'reset' });
    navigate(
      `/controlling/umsatzplanung/${routeParams.jahr}/${routeParams.monat}?success=true`,
    );
  }

  function onError(message?: string) {
    dispatch({
      type: 'updateDialogMessage',
      dialogMessage: {
        message:
          message ??
          t('domain.controlling.umsatzplanung.planung.errors.unknownError'),
        type: 'error',
      },
    });
  }

  const { handleSubmit } = useFormSubmit(onSuccess, onError);

  useEffect(() => {
    setHasChanges(planwerteForm.hasChanges);
  }, [setHasChanges, planwerteForm.hasChanges]);

  return (
    <>
      <div className="flex flex-col gap-6 pt-8">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-neutrals-900">
            {t('domain.controlling.umsatzplanung.planung.dialog.title')}
          </DialogTitle>
        </DialogHeader>
        <div>
          <div className="flex gap-8 h-full">
            <div className="flex mt-[9px] pb-8 flex-col gap-8">
              <div className="flex flex-col gap-2">
                <div className="text-xs font-bold">
                  {getMonthNameByDateObject(new Date(datum))}{' '}
                  {dayjs(datum).year()}
                </div>
                <div className="text-xs text-neutrals-700">
                  {t(
                    'domain.controlling.umsatzplanung.planung.dialog.validPlanwerteSubtitle',
                    {
                      validPlanwerteCount,
                      daysInMonth: dayjs(datum).daysInMonth(),
                    },
                  )}
                </div>
              </div>
              <Calendar
                selected={new Date(datum)}
                month={new Date(datum)}
                mode="single"
                components={{
                  Day: ({ date }: DayProps) => {
                    const isDateInCurrentMonth =
                      date.getMonth() === dayjs(datum).get('month');

                    const currentPlanwert = planwerte.find(
                      (planwert) =>
                        planwert.datum === dayjs(date).format('YYYY-MM-DD'),
                    );

                    return isDateInCurrentMonth ? (
                      <Day
                        currentPlanwert={currentPlanwert}
                        currentDate={date}
                        selectedDate={new Date(datum)}
                        absatzVorgabe={
                          tagebuchEinstellungen.absatzProduktivitaet
                            .absatzVorgabe
                        }
                      />
                    ) : null;
                  },
                }}
              />
            </div>
            <div className="h-full w-px bg-neutrals-300" />
            <div className="grow flex flex-col gap-2 justify-start">
              <Header />
              <div className="flex gap-4 flex-col overflow-auto h-[500px]">
                <EditTable />
                <WithFeatureFlags name="mindestbelegung" Fallback={<></>}>
                  <Mindestbelegung datum={datum} />
                </WithFeatureFlags>
                <Suspense fallback={<></>}>
                  <TagesInformationen standortId={standortId} />
                </Suspense>
              </div>
            </div>
          </div>
        </div>
      </div>
      <hr className="h-px bg-neutrals-300 border-0 -mx-8"></hr>
      <div className="flex items-center justify-between py-4">
        <DialogFooterMessage
          dialogFooterMessage={planwerteForm.dialogMessage}
          isUpdating={planwerteForm.isSubmitting}
        />
        <Button
          onClick={(e) => {
            // Damit können auch die Default-Werte für AbsatzProduktivität und UmsatzProduktivität gespeichert werden
            if (!planwerteForm.isDirty && !planwerteForm.hasChanges) {
              handleSubmit(e);
            } else if (planwerteForm.hasChanges) {
              handleSubmit(e);
            } else {
              handleClose();
            }
          }}
          disabled={planwerteForm.isSubmitting}>
          {t('domain.controlling.umsatzplanung.ziele.fertig')}
        </Button>
      </div>
    </>
  );
}
